import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import TutorialModal from '../../components/feature/TutorialModal';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import AchievementForm from './components/AchievementForm';
import AchievementList from './components/AchievementList';

interface StatItem {
  label: string;
  value: string;
  icon: string;
}

export default function HomePage() {
  const { user } = useAuth();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [showAchievementForm, setShowAchievementForm] = useState(false);
  const [achievementRefreshKey, setAchievementRefreshKey] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    support_type: 'advice'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [stats, setStats] = useState<StatItem[]>([
    { label: '勉強日記', value: '...', icon: 'ri-book-2-line' },
    { label: '勉強資料', value: '...', icon: 'ri-file-text-line' },
    { label: '講義動画', value: '...', icon: 'ri-video-line' },
    { label: '一問一答', value: '...', icon: 'ri-question-answer-line' },
  ]);
  const [subjects, setSubjects] = useState<string[]>([]);

  useEffect(() => {
    const fetchStats = async () => {
      const [diary, materials, videos, quiz] = await Promise.all([
        supabase.from('diary_entries').select('id', { count: 'exact', head: true }),
        supabase.from('study_materials').select('id', { count: 'exact', head: true }),
        supabase.from('lecture_videos').select('id', { count: 'exact', head: true }),
        supabase.from('quiz_questions').select('id', { count: 'exact', head: true }),
      ]);
      setStats([
        { label: '勉強日記', value: `${diary.count ?? 0}件`, icon: 'ri-book-2-line' },
        { label: '勉強資料', value: `${materials.count ?? 0}件`, icon: 'ri-file-text-line' },
        { label: '講義動画', value: `${videos.count ?? 0}件`, icon: 'ri-video-line' },
        { label: '一問一答', value: `${quiz.count ?? 0}問`, icon: 'ri-question-answer-line' },
      ]);
    };

    const fetchSubjects = async () => {
      try {
        const { data: categories } = await supabase
          .from('user_categories')
          .select('name')
          .eq('is_favorite', true)
          .order('name');

        if (categories) {
          setSubjects(categories.map(c => c.name));
        }
      } catch (error) {
        console.error('科目データの取得に失敗しました:', error);
      }
    };

    fetchStats();
    fetchSubjects();
  }, []);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formData = new URLSearchParams();
      formData.append('name', contactFormData.name);
      formData.append('email', contactFormData.email);
      formData.append('subject', contactFormData.subject);
      formData.append('message', contactFormData.message);
      formData.append('support_type', contactFormData.support_type);

      const response = await fetch('https://readdy.ai/api/form/d6qlf25sbgrc47fim7v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        // 自動返信メール送信
        try {
          const { supabase: supabaseClient } = await import('../../lib/supabase');
          await supabaseClient.functions.invoke('send-contact-reply', {
            body: {
              name: contactFormData.name,
              email: contactFormData.email,
              subject: contactFormData.subject,
            },
          });
        } catch (replyErr) {
          console.warn('Auto-reply sending failed (non-critical):', replyErr);
        }

        setSubmitStatus('success');
        setContactFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          support_type: 'advice'
        });
        setTimeout(() => {
          setShowContactForm(false);
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('送信エラー:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const features = [
    {
      icon: 'ri-book-2-line',
      title: '勉強日記',
      desc: '毎日の学習内容・時間・気づきを記録。振り返りで学習効率を高めます。',
      path: '/diary',
      color: 'from-teal-500 to-emerald-600',
    },
    {
      icon: 'ri-file-text-line',
      title: '勉強資料',
      desc: '科目ごとに整理した学習資料をいつでも参照。知識の体系化に役立てます。',
      path: '/materials',
      color: 'from-blue-500 to-cyan-600',
    },
    {
      icon: 'ri-video-line',
      title: '講義動画',
      desc: 'YouTubeの講義動画をまとめて管理。効率的な動画学習をサポートします。',
      path: '/videos',
      color: 'from-purple-500 to-violet-600',
    },
    {
      icon: 'ri-question-answer-line',
      title: '一問一答クイズ',
      desc: '自作の問題で繰り返し復習。習熟度を可視化して弱点を克服します。',
      path: '/quiz',
      color: 'from-orange-500 to-amber-600',
    },
    {
      icon: 'ri-bar-chart-2-line',
      title: 'ダッシュボード',
      desc: '学習時間・進捗・ストリークを一目で確認。モチベーション維持に活用します。',
      path: '/dashboard',
      color: 'from-rose-500 to-pink-600',
    },
    {
      icon: 'ri-microscope-line',
      title: '研究経過報告',
      desc: '研究の進捗・成果・文献をまとめて管理。研究活動を体系的に記録します。',
      path: '/portfolio',
      color: 'from-indigo-500 to-blue-600',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 sm:py-20 lg:py-32">
        {/* Background image */}
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/096ad60cdd04e8f7fee02e30e7bee072.jpg"
            alt="永山の勉強アーカイブ"
            className="w-full h-full object-cover object-top hero-bg-scale"
          />
          <div className="absolute inset-0 hero-overlay-animated"></div>
          {/* Extra dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/50"></div>
        </div>

        {/* Floating orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-96 h-96 bg-gradient-to-br from-teal-400/20 to-emerald-400/10 rounded-full blur-3xl top-[10%] left-[5%] hero-orb-1"></div>
          <div className="absolute w-[500px] h-[500px] bg-gradient-to-br from-emerald-400/15 to-teal-400/5 rounded-full blur-3xl top-[30%] right-[-10%] hero-orb-2"></div>
          <div className="absolute w-80 h-80 bg-gradient-to-br from-teal-300/20 to-emerald-300/10 rounded-full blur-3xl bottom-[15%] left-[15%] hero-orb-3"></div>
          <div className="absolute w-64 h-64 border border-teal-400/10 rounded-full top-[10%] left-[5%] animate-float-slow"></div>
          <div className="absolute w-96 h-96 border border-white/5 rounded-full top-[30%] right-[-5%] animate-float-medium"></div>
          <div className="absolute w-40 h-40 border border-emerald-400/10 rounded-full bottom-[15%] left-[15%] animate-float-fast"></div>
          <div className="absolute w-[600px] h-1 bg-gradient-to-r from-transparent via-teal-400/30 to-transparent top-[20%] left-[-200px] hero-streak" style={{ animationDelay: '0s' }}></div>
          <div className="absolute w-[500px] h-1 bg-gradient-to-r from-transparent via-emerald-400/25 to-transparent top-[50%] right-[-150px] hero-streak" style={{ animationDelay: '2s' }}></div>
          <div className="absolute w-[700px] h-1 bg-gradient-to-r from-transparent via-teal-300/20 to-transparent top-[70%] left-[-250px] hero-streak" style={{ animationDelay: '4s' }}></div>
          {[
            { top: '20%', left: '15%', delay: '0s', size: 'w-2 h-2', color: 'bg-teal-400/40' },
            { top: '35%', left: '75%', delay: '1s', size: 'w-3 h-3', color: 'bg-white/20' },
            { top: '60%', left: '25%', delay: '2s', size: 'w-1.5 h-1.5', color: 'bg-teal-300/30' },
            { top: '45%', left: '85%', delay: '0.5s', size: 'w-2.5 h-2.5', color: 'bg-white/15' },
            { top: '75%', left: '55%', delay: '1.5s', size: 'w-2 h-2', color: 'bg-teal-400/25' },
            { top: '15%', left: '45%', delay: '2.5s', size: 'w-1.5 h-1.5', color: 'bg-white/25' },
          ].map((p, i) => (
            <div
              key={i}
              className={`absolute ${p.size} ${p.color} rounded-full hero-particle-float`}
              style={{ top: p.top, left: p.left, animationDelay: p.delay }}
            ></div>
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 text-center pt-20 sm:pt-24 pb-12 sm:pb-16">
          {/* Tutorial button - 右上に配置 */}
          <div className="absolute top-4 right-4 sm:top-6 sm:right-6 animate-hero-fade-up">
            <button
              onClick={() => setIsTutorialOpen(true)}
              className="group flex items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-sm text-white text-sm font-semibold rounded-xl border border-white/30 hover:bg-white/20 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              <i className="ri-question-line text-lg group-hover:rotate-12 transition-transform duration-300"></i>
              <span className="hidden sm:inline">使い方ガイド</span>
            </button>
          </div>

          <div className="animate-hero-fade-up">
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 bg-teal-500/20 border border-teal-400/40 rounded-full text-teal-200 text-xs sm:text-sm font-medium mb-4 sm:mb-6 backdrop-blur-sm">
              <i className="ri-sparkling-line text-sm sm:text-base"></i>
              <span className="hidden xs:inline">あなたの学習を、最高の環境でサポートする</span>
              <span className="xs:hidden">最高の学習環境をサポート</span>
            </span>
          </div>

          {/* Logo Title */}
          <div className="animate-hero-fade-up-delay mb-4 sm:mb-6">
            {/* Decorative top line */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 mb-4 sm:mb-6">
              <div className="h-px w-12 sm:w-20 bg-gradient-to-r from-transparent via-teal-400/60 to-teal-400/80"></div>
              <span className="text-teal-300/80 text-[0.65rem] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase font-light">Study Archive</span>
              <div className="h-px w-12 sm:w-20 bg-gradient-to-l from-transparent via-teal-400/60 to-teal-400/80"></div>
            </div>

            {/* Main Logo - スマホでは縦並び、PCでは横並び */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-3 sm:mb-4">
              {/* Icon with glow */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-400 rounded-xl sm:rounded-2xl blur-xl sm:blur-2xl opacity-60"></div>
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 flex items-center justify-center bg-gradient-to-br from-teal-400 via-teal-300 to-emerald-400 rounded-xl sm:rounded-2xl shadow-2xl">
                  <i className="ri-book-open-line text-white text-2xl sm:text-3xl lg:text-4xl"></i>
                </div>
              </div>

              {/* Title Text */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
                <span className="relative inline-block">
                  {/* Glow layer */}
                  <span
                    className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-r from-teal-200 via-white to-emerald-200 blur-md opacity-70 select-none"
                    aria-hidden="true"
                  >
                    永山の勉強アーカイブ
                  </span>
                  {/* Main text */}
                  <span className="relative text-transparent bg-clip-text bg-gradient-to-r from-teal-200 via-white to-emerald-200"
                    style={{ 
                      filter: 'drop-shadow(0 0 40px rgba(45,212,191,0.5)) drop-shadow(0 2px 12px rgba(0,0,0,0.9))'
                    }}>
                    永山の勉強アーカイブ
                  </span>
                </span>
              </h1>
            </div>

            {/* Decorative bottom accent */}
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-5">
              <div className="h-px w-20 sm:w-32 bg-gradient-to-r from-transparent via-teal-400/50 to-teal-400/70"></div>
              <div className="flex items-center gap-1.5 sm:gap-2">
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-teal-400/70"></div>
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-teal-300/80"></div>
                <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-teal-400/70"></div>
              </div>
              <div className="h-px w-20 sm:w-32 bg-gradient-to-l from-transparent via-teal-400/50 to-teal-400/70"></div>
            </div>
          </div>

          <p className="animate-hero-fade-up-delay2 text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-10 max-w-2xl mx-auto leading-relaxed px-2"
            style={{ textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}>
            学びたいすべての人に、最適な環境を。<br />
            日記・資料・動画・クイズで、あなたの学習を丁寧にサポートします。
          </p>

          <div className="animate-hero-fade-up-delay2 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-2">
            <Link
              to="/diary"
              className="px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm sm:text-base font-bold rounded-xl hover:from-teal-400 hover:to-emerald-500 transition-all shadow-lg shadow-teal-500/30 hover:shadow-teal-500/50 hover:-translate-y-0.5 duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-book-open-line text-base sm:text-lg"></i>
              勉強日記を見る
            </Link>
            {!user && (
              <Link
                to="/login"
                className="px-6 sm:px-8 py-3 sm:py-3.5 bg-white/10 backdrop-blur-sm text-white text-sm sm:text-base font-bold rounded-xl border border-white/30 hover:bg-white/20 transition-all hover:-translate-y-0.5 duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-login-box-line text-base sm:text-lg"></i>
                ログイン
              </Link>
            )}
            {user && (
              <Link
                to="/dashboard"
                className="px-6 sm:px-8 py-3 sm:py-3.5 bg-white/10 backdrop-blur-sm text-white text-sm sm:text-base font-bold rounded-xl border border-white/30 hover:bg-white/20 transition-all hover:-translate-y-0.5 duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
              >
                <i className="ri-dashboard-line text-base sm:text-lg"></i>
                ダッシュボード
              </Link>
            )}
          </div>

          {/* Stats - スマホで2×2グリッド、視認性向上 */}
          <div className="animate-hero-fade-up-delay2 mt-10 sm:mt-14 grid grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto px-2">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-3 sm:p-4 text-center hover:bg-white/15 transition-all duration-300 cursor-pointer"
              >
                <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center mx-auto mb-1.5 sm:mb-2">
                  <i className={`${stat.icon} text-teal-300 text-lg sm:text-xl`}></i>
                </div>
                <p className="text-white font-bold text-base sm:text-lg" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.6)' }}>{stat.value}</p>
                <p className="text-white/70 text-[0.7rem] sm:text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator - スマホでは小さく */}
        <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 sm:gap-2 text-white/50">
          <span className="text-[0.65rem] sm:text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-4 h-7 sm:w-5 sm:h-8 border border-white/30 rounded-full flex items-start justify-center pt-1.5">
            <div className="w-0.5 h-1.5 sm:w-1 sm:h-2 bg-white/50 rounded-full animate-scroll-dot"></div>
          </div>
        </div>
      </section>

      {/* サイト説明セクション */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-white via-teal-50/30 to-emerald-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <span className="inline-block px-3 sm:px-4 py-1.5 bg-teal-500/10 border border-teal-500/20 text-teal-700 dark:text-teal-300 text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4">
              <i className="ri-information-line mr-1"></i>
              このサイトについて
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 sm:mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-gray-700 dark:from-slate-300 dark:to-gray-200">お願い</span>
            </h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 lg:p-10 mb-6">
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 leading-relaxed mb-4">
                <strong className="text-teal-600 dark:text-teal-400">永山の勉強アーカイブ</strong>は、資格試験の学習から大学での研究活動まで、あらゆる学びを一元管理できる学習記録サイトです。
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 my-6 sm:my-8">
                <div className="bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-xl p-4 sm:p-5 border border-teal-100 dark:border-teal-800">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-500 rounded-lg flex-shrink-0">
                      <i className="ri-book-open-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">日々の学習を記録</h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        勉強日記で毎日の学習内容・時間・気づきを記録。振り返りで学習効率を高めます。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-xl p-4 sm:p-5 border border-blue-100 dark:border-blue-800">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-500 rounded-lg flex-shrink-0">
                      <i className="ri-file-list-3-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">資料を体系的に整理</h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        科目ごとに学習資料を整理。いつでも必要な情報にアクセスできます。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl p-4 sm:p-5 border border-purple-100 dark:border-purple-800">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-purple-500 rounded-lg flex-shrink-0">
                      <i className="ri-question-answer-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">クイズで知識定着</h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        自作の一問一答で繰り返し復習。習熟度を可視化して弱点を克服します。
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-xl p-4 sm:p-5 border border-indigo-100 dark:border-indigo-800">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-indigo-500 rounded-lg flex-shrink-0">
                      <i className="ri-microscope-line text-white text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">研究活動を記録</h3>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                        研究テーマ・文献・進捗を体系的に管理。研究室に所属していなくても自分なりの研究が可能です。
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-teal-500/10 via-emerald-500/10 to-cyan-500/10 dark:from-teal-500/20 dark:via-emerald-500/20 dark:to-cyan-500/20 rounded-xl p-4 sm:p-6 border border-teal-200 dark:border-teal-700 my-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-flash-line text-teal-600 dark:text-teal-400 text-2xl"></i>
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">こんな方におすすめ</h4>
                    <ul className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 space-y-1.5 leading-relaxed">
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0"></i>
                        <span>資格試験や大学受験に向けて計画的に学習したい方</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0"></i>
                        <span>研究室に所属せず独自に研究活動を進めたい方</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0"></i>
                        <span>学習の進捗を可視化してモチベーションを維持したい方</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-check-line text-teal-600 dark:text-teal-400 mt-0.5 flex-shrink-0"></i>
                        <span>複数の科目や分野を並行して学習している方</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                すべての機能は無料でご利用いただけます。ログインして、今日から自分だけの学習アーカイブを作り始めましょう。
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm sm:text-base font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-300 whitespace-nowrap"
            >
              <i className="ri-login-box-line text-lg"></i>
              今すぐ始める
            </Link>
          </div>
        </div>
      </section>

      {/* 自己紹介と支援セクション - 新規追加 */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-slate-50 via-gray-50 to-slate-100 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10">
            <span className="inline-block px-3 sm:px-4 py-1.5 bg-slate-500/10 border border-slate-500/20 text-slate-700 dark:text-slate-300 text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4">
              <i className="ri-user-heart-line mr-1"></i>
              運営者について
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 sm:mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-slate-600 to-gray-700 dark:from-slate-300 dark:to-gray-200">お願い</span>
            </h2>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 p-6 sm:p-8 lg:p-10 mb-6">
            <div className="prose prose-slate dark:prose-invert max-w-none">

              <div className="space-y-4 text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                <p>
                  私は大阪在住の大学生です。将来的には<strong className="text-slate-700 dark:text-slate-300">中小企業診断士と税理士</strong>の双方を視野に入れ、中小企業支援と事業承継の実務・研究の両面に携わることを目指しています。
                </p>
                
                <p>
                  特に関心を持っているのは、<strong className="text-slate-700 dark:text-slate-300">地域の家業経営や中小企業における事業承継</strong>です。なかでも、相続税・固定資産税などの資産課税や事業承継税制が、経営者や家族の承継判断にどのような影響を与えているのかというテーマに強い問題意識を持っています。
                </p>

                <div className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-slate-900/50 dark:to-gray-900/50 rounded-xl p-4 sm:p-5 border border-slate-200 dark:border-slate-700 my-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-book-open-line text-slate-600 dark:text-slate-400"></i>
                    現在の取り組み
                  </h3>
                  <p className="text-sm sm:text-base">
                    制度や会計・税務の学習に加え、事業承継に関する制度資料・白書・先行研究の収集、そして将来的な実証研究を見据えたデータ分析の勉強にも取り組んでいます。単に資格試験の勉強をするだけでなく、現実の中小企業が抱える悩みを、制度・税制・経営の交点から捉えたいと考えています。
                  </p>
                </div>

                <p>
                  地域経済や地域産業への関心から、<strong className="text-slate-700 dark:text-slate-300">地域経済</strong>にも注目しています。黒字であっても承継が難しい家業、資産を持つがゆえに生じる税負担、地域に根差した事業が次世代へ引き継がれにくい現実――そうした問題を、感覚ではなく、制度理解とデータに基づいて考えられる人間になりたいと思っています。
                </p>

                <div className="bg-gradient-to-r from-teal-50 via-emerald-50 to-cyan-50 dark:from-teal-900/20 dark:via-emerald-900/20 dark:to-cyan-900/20 rounded-xl p-4 sm:p-5 border border-teal-200 dark:border-teal-700 my-6">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <i className="ri-mail-send-line text-teal-600 dark:text-teal-400"></i>
                    ご連絡・ご助言をお待ちしています
                  </h3>
                  <p className="text-sm sm:text-base mb-3">
                    もし、<strong>事業承継、資産課税、地域経済、中小企業支援、会計・税務実務、実証分析</strong>などの分野に知見をお持ちの方がいらっしゃいましたら、ぜひご連絡いただけると嬉しいです。研究の進め方、参考文献、実務上の視点、現場の課題など、どのような形でも学ばせていただきたいと考えています。
                  </p>
                </div>

                <p>
                  将来には、文献収集、現地調査、データ整備、研究発信などに継続的に取り組み、事業承継で悩む地域の家業や中小企業にとって少しでも意味のある知見を還元できる形を目指しています。
                </p>
              </div>

              {/* 支援の使い道 */}
              <div className="mt-7 rounded-xl border border-slate-200 dark:border-slate-600 overflow-hidden">
                <div className="bg-slate-700 dark:bg-slate-800 px-4 sm:px-5 py-3">
                  <p className="text-sm font-bold text-white flex items-center gap-2">
                    <i className="ri-heart-fill text-rose-400 text-base"></i>
                    応援してくださった方への感謝と、その使い道
                  </p>
                </div>
                <div className="px-4 sm:px-5 py-4 bg-slate-50 dark:bg-slate-900/40">
                  <ul className="space-y-2.5 text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                    {[
                      { icon: 'ri-book-3-line', text: '参考文献・専門書の購入費' },
                      { icon: 'ri-file-chart-line', text: '統計データ・調査資料の取得費' },
                      { icon: 'ri-road-map-line', text: '現地調査・フィールドワーク費' },
                      { icon: 'ri-megaphone-line', text: '研究成果の発信・公開費' },
                    ].map((item) => (
                      <li key={item.text} className="flex items-center gap-2.5">
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 bg-teal-500/10 rounded-md">
                          <i className={`${item.icon} text-teal-600 dark:text-teal-400 text-sm`}></i>
                        </div>
                        <span>{item.text}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
                    研究の各段階でいただいた応援が、直接このサイトのコンテンツや将来の発信に活きていきます。
                    一円でも、「応援しています」というお気持ちだけでも、本当に力になります。
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={() => setShowContactForm(true)}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-600 to-gray-700 text-white text-sm sm:text-base font-bold rounded-xl hover:from-slate-700 hover:to-gray-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-300 whitespace-nowrap"
              >
                <i className="ri-mail-line text-lg"></i>
                ご連絡・ご助言はこちら
              </button>
              <a
                href="https://paypal.me/nagayamagiren"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white text-sm sm:text-base font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 duration-300 whitespace-nowrap"
              >
                <i className="ri-heart-line text-lg"></i>
                研究活動を応援する
              </a>
            </div>

            <p className="text-xs sm:text-sm text-center text-gray-500 dark:text-gray-400 mt-6">
              どうぞよろしくお願いします。
            </p>
          </div>
        </div>
      </section>

      {/* 掲載中の科目セクション */}
      {subjects.length > 0 && (
        <section className="py-12 sm:py-16 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white mb-2">
                掲載中の科目
              </h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base">
                現在このサイトで学習されている科目一覧
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {subjects.map((subject, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                >
                  {subject}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 達成報告セクション */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-br from-white via-teal-50/30 to-emerald-50/30 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block px-3 sm:px-4 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4">
              達成報告
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 sm:mb-4 px-4">
              みんなの<span className="text-teal-600 dark:text-teal-400">達成報告</span>
            </h2>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto px-4 mb-6">
              資格試験合格や研究成果など、目標達成の喜びをシェアしましょう
            </p>
            
            {user && (
              <button
                onClick={() => setShowAchievementForm(true)}
                className="px-6 py-3 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all shadow-lg font-bold flex items-center justify-center gap-2 mx-auto whitespace-nowrap"
              >
                <i className="ri-trophy-line text-lg"></i>
                達成報告を投稿
              </button>
            )}
          </div>

          <AchievementList limit={6} refreshKey={achievementRefreshKey} />

          <div className="text-center mt-8">
            <Link
              to="/ranking"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-semibold whitespace-nowrap"
            >
              すべての達成報告を見る
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 lg:py-20 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 sm:mb-14">
            <span className="inline-block px-3 sm:px-4 py-1.5 bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 text-xs sm:text-sm font-semibold rounded-full mb-3 sm:mb-4">
              コンテンツ
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 dark:text-white mb-3 sm:mb-4 px-4">
              学習を支える<span className="text-teal-600 dark:text-teal-400">6つの機能</span>
            </h2>
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 max-w-xl mx-auto px-4">
              記録・整理・復習・可視化のすべてをこのサイトで完結させます。
            </p>
          </div>

          {/* スマホでは1カラム、タブレットで2カラム、PCで3カラム */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f) => (
              <Link
                key={f.path}
                to={f.path}
                className="group bg-white dark:bg-gray-800 rounded-2xl p-5 sm:p-6 shadow-sm border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              >
                <div className={`w-11 h-11 sm:w-12 sm:h-16 lg:w-20 lg:h-20 flex items-center justify-center bg-gradient-to-br ${f.color} rounded-xl mb-3 sm:mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <i className={`${f.icon} text-white text-lg sm:text-xl`}></i>
                </div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-3">{f.desc}</p>
                <div className="flex items-center gap-1 text-teal-600 dark:text-teal-400 text-xs sm:text-sm font-medium">
                  <span>詳しく見る</span>
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 sm:py-20 px-4 sm:px-6 overflow-hidden bg-gradient-to-br from-gray-900 via-teal-900 to-emerald-900">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute w-64 h-64 bg-teal-500/20 rounded-full blur-3xl top-0 left-1/4 hero-orb-1"></div>
          <div className="absolute w-48 h-48 bg-emerald-500/20 rounded-full blur-3xl bottom-0 right-1/4 hero-orb-2"></div>
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '28px 28px' }}></div>
        </div>
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-teal-500/20 border border-teal-400/30 rounded-full text-teal-300 text-xs sm:text-sm font-medium mb-4 sm:mb-6">
            <i className="ri-sparkling-line"></i>
            今日も一歩前へ
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-3 sm:mb-4 px-4 drop-shadow-lg">
            今日の学習を記録しよう
          </h2>
          <p className="text-gray-300 mb-6 sm:mb-8 text-sm sm:text-base lg:text-lg leading-relaxed px-4">
            一歩一歩の積み重ねが、あなたの可能性を広げていく。<br className="hidden sm:inline" />
            最適な環境で、自分だけの学習スタイルを築こう。
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
            <Link
              to="/diary"
              className="px-6 sm:px-8 py-3 sm:py-3.5 bg-gradient-to-r from-teal-400 to-emerald-500 text-white text-sm sm:text-base font-bold rounded-xl hover:from-teal-300 hover:to-emerald-400 transition-all shadow-lg shadow-teal-500/30 hover:-translate-y-0.5 duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-pencil-line text-base sm:text-lg"></i>
              日記を書く
            </Link>
            <Link
              to="/quiz"
              className="px-6 sm:px-8 py-3 sm:py-3.5 bg-white/10 backdrop-blur-sm text-white text-sm sm:text-base font-bold rounded-xl border border-white/30 hover:bg-white/20 transition-all hover:-translate-y-0.5 duration-300 flex items-center justify-center gap-2 whitespace-nowrap"
            >
              <i className="ri-question-answer-line text-base sm:text-lg"></i>
              クイズで復習
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {/* Tutorial Modal */}
      <TutorialModal isOpen={isTutorialOpen} onClose={() => setIsTutorialOpen(false)} />

      {/* Achievement Form Modal */}
      {showAchievementForm && (
        <AchievementForm
          onSuccess={() => {
            setShowAchievementForm(false);
            setAchievementRefreshKey(prev => prev + 1);
          }}
          onCancel={() => setShowAchievementForm(false)}
        />
      )}

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-slate-600 to-gray-700 text-white px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <i className="ri-mail-send-line text-xl"></i>
                </div>
                <h3 className="text-xl font-bold">ご連絡・ご助言フォーム</h3>
              </div>
              <button
                onClick={() => setShowContactForm(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <form onSubmit={handleContactSubmit} className="p-6 space-y-5" data-readdy-form>
              <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-700 rounded-xl p-4">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  研究の進め方、参考文献、実務上の視点、現場の課題など、どのような形でもご助言いただけると幸いです。また、研究活動へのご支援についてもお気軽にお問い合わせください。
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  お名前 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={contactFormData.name}
                  onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="山田太郎"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={contactFormData.email}
                  onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  お問い合わせ種別 <span className="text-red-500">*</span>
                </label>
                <select
                  name="support_type"
                  value={contactFormData.support_type}
                  onChange={(e) => setContactFormData({ ...contactFormData, support_type: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="advice">研究・学習に関するご助言</option>
                  <option value="collaboration">共同研究・協力のご提案</option>
                  <option value="support">研究活動へのご支援について</option>
                  <option value="other">その他</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  件名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  value={contactFormData.subject}
                  onChange={(e) => setContactFormData({ ...contactFormData, subject: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="事業承継研究に関するご助言"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  メッセージ <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="message"
                  value={contactFormData.message}
                  onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  placeholder="ご助言やご提案の内容をご記入ください（500文字以内）"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {contactFormData.message.length} / 500文字
                </p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-check-line text-green-600 dark:text-green-400 text-xl"></i>
                  <div>
                    <p className="text-sm font-semibold text-green-700 dark:text-green-300">
                      送信が完了しました。ありがとうございます！
                    </p>
                    <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                      ご入力のメールアドレスに受取確認メールをお送りしました。
                    </p>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-xl p-4 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-600 dark:text-red-400 text-xl"></i>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-semibold"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-slate-600 to-gray-700 text-white rounded-xl hover:from-slate-700 hover:to-gray-800 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill"></i>
                      送信する
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}